import { useAuth } from '@clerk/clerk-react';
import type { ReactNode } from 'react';
import { Navigate } from 'react-router';
import { LoginScreen } from './LoginScreen';
import { useMember } from './useMember';
import { Spinner } from '../ui/Spinner';

function Loading() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-canvas text-tertiary">
      <Spinner label="Loading" />
    </div>
  );
}

/** Gates everything behind a signed-in device credential. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn } = useAuth();

  if (!isLoaded) return <Loading />;
  if (!isSignedIn) return <LoginScreen />;
  return <>{children}</>;
}

/**
 * Admin routes.
 *
 * The role lives in the JWT and is enforced by the API on every request — this
 * check only decides what to render, and must never be mistaken for the
 * security boundary. A `user` credential that navigates to /admin by hand gets
 * bounced here and would be rejected by the server regardless.
 */
export function RequireAdmin({ children }: { children: ReactNode }) {
  const member = useMember();

  if (!member) return <Loading />;
  if (member.role !== 'admin') return <Navigate to="/" replace />;
  return <>{children}</>;
}
